import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wind, Flame, Clock, Phone, MapPin, Check, Loader2, Sparkles, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const PROMO_PACKAGES = [
  {
    id: "kitesurf_prima_esperienza",
    icon: "🪁",
    title: "Kitesurf — Prima Esperienza",
    subtitle: "1 lezione · solo principianti",
    promoPrice: "59,99",
    normalPrice: "90€/h in Nord Sardegna",
    badge: "BEST DEAL",
    color: "from-red-500 to-orange-500",
  },
  {
    id: "kitesurf_semiprivato",
    icon: "🪁",
    title: "Kitesurf Semi-Privato",
    subtitle: "Lezione condivisa",
    promoPrice: "40",
    normalPrice: null,
    badge: null,
    color: "from-orange-500 to-amber-500",
  },
  {
    id: "wingsurf_privato",
    icon: "🏄",
    title: "Wingsurf — Lezione Privata",
    subtitle: "Facile e divertente",
    promoPrice: "59,99",
    normalPrice: null,
    badge: "TOP",
    color: "from-cyan-500 to-blue-500",
  },
  {
    id: "wingsurf_noleggio",
    icon: "🏄",
    title: "Wingsurf — Noleggio",
    subtitle: "Per chi è già autonomo",
    promoPrice: "20",
    normalPrice: null,
    badge: null,
    color: "from-blue-500 to-indigo-500",
  },
  {
    id: "wingsurf_gruppo_2",
    icon: "👥",
    title: "Wingsurf Gruppo · 2 persone",
    subtitle: "€/persona",
    promoPrice: "50",
    normalPrice: null,
    badge: null,
    color: "from-teal-500 to-cyan-500",
  },
  {
    id: "wingsurf_gruppo_3",
    icon: "👥",
    title: "Wingsurf Gruppo · 3 persone",
    subtitle: "€/persona",
    promoPrice: "40",
    normalPrice: null,
    badge: null,
    color: "from-emerald-500 to-teal-500",
  },
  {
    id: "windsurf_privato",
    icon: "🌊",
    title: "Windsurf — Lezione Privata",
    subtitle: "Solo principianti",
    promoPrice: "59,99",
    normalPrice: null,
    badge: null,
    color: "from-sky-500 to-cyan-500",
  },
  {
    id: "kids_windsurf_giocosurf",
    icon: "🧒",
    title: "Kids 5-9 anni · Windsurf/Gioco-Surf",
    subtitle: "30 minuti",
    promoPrice: "30",
    normalPrice: null,
    badge: "KIDS",
    color: "from-pink-500 to-rose-500",
  },
  {
    id: "kids_windsurf_noleggio",
    icon: "🧒",
    title: "Kids 5-9 anni · Noleggio Windsurf",
    subtitle: "1 ora",
    promoPrice: "20",
    normalPrice: null,
    badge: null,
    color: "from-fuchsia-500 to-pink-500",
  },
];

const COUNTRIES = [
  { code: "IT", flag: "🇮🇹", name: "Italia" },
  { code: "FR", flag: "🇫🇷", name: "Francia" },
  { code: "DE", flag: "🇩🇪", name: "Germania" },
  { code: "ES", flag: "🇪🇸", name: "Spagna" },
  { code: "GB", flag: "🇬🇧", name: "Regno Unito" },
  { code: "US", flag: "🇺🇸", name: "Stati Uniti" },
  { code: "CH", flag: "🇨🇭", name: "Svizzera" },
  { code: "AT", flag: "🇦🇹", name: "Austria" },
  { code: "BE", flag: "🇧🇪", name: "Belgio" },
  { code: "NL", flag: "🇳🇱", name: "Paesi Bassi" },
  { code: "PT", flag: "🇵🇹", name: "Portogallo" },
  { code: "PL", flag: "🇵🇱", name: "Polonia" },
  { code: "OTHER", flag: "🌍", name: "Altro" },
];

export default function PromoPage() {
  const [isActive, setIsActive] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", surname: "", phone: "", email: "", country: "IT", num_people: 1, notes: "",
  });

  useEffect(() => {
    axios.get(`${API}/promo/status`)
      .then(r => setIsActive(r.data?.active))
      .catch(() => setIsActive(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Seleziona prima un pacchetto promo!");
      return;
    }
    if (!form.name || !form.surname || !form.phone || !form.email) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/promo/booking`, { ...form, activity: selected.id });
      setSubmitted(true);
      toast.success("Richiesta inviata! Ti chiamiamo durante la giornata.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (isActive === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
      </div>
    );
  }

  // Promo inactive
  if (!isActive) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="max-w-md text-center bg-slate-800/50 backdrop-blur-xl rounded-3xl p-10 border border-slate-700"
          data-testid="promo-inactive"
        >
          <AlertCircle className="w-16 h-16 text-orange-400 mx-auto mb-4" />
          <h1 className="text-3xl font-black text-white mb-3">Promo non attiva</h1>
          <p className="text-slate-300 mb-6">
            La promo <strong className="text-orange-400">NOWIND LAST MINUTES</strong> non è attiva al momento. Torna a trovarci o seguici sui social!
          </p>
          <a href="https://sardiniakiteschool.net" className="inline-block px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-full hover:scale-105 transition">
            Vai al sito
          </a>
        </motion.div>
      </div>
    );
  }

  // Submitted success
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 flex items-center justify-center p-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="max-w-md text-center bg-white/95 rounded-3xl p-10 shadow-2xl"
          data-testid="promo-success"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-white" strokeWidth={3} />
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-3">Sei in lista! 🎉</h1>
          <p className="text-slate-600 mb-6">
            Resta in zona spiaggia! Ti contatteremo durante la giornata appena il vento termico sarà disponibile per fare la lezione.
          </p>
          <Button
            onClick={() => { setSubmitted(false); setSelected(null); setForm({ name: "", surname: "", phone: "", email: "", country: "IT", num_people: 1, notes: "" }); }}
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white"
            data-testid="promo-new-request"
          >
            Nuova richiesta
          </Button>
        </motion.div>
      </div>
    );
  }

  // Main promo page
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 text-white overflow-hidden">
      {/* Animated background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-orange-500 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute top-1/2 -right-40 w-96 h-96 bg-red-500 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute -bottom-40 left-1/3 w-96 h-96 bg-amber-500 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* HERO */}
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/20 border border-orange-500/30 mb-4">
            <Flame className="w-4 h-4 text-orange-400 animate-pulse" />
            <span className="text-xs font-bold tracking-widest text-orange-300 uppercase">Offerta Limitata · Sardinia Kite School</span>
          </div>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter mb-3 leading-none" data-testid="promo-title">
            <span className="block bg-gradient-to-r from-orange-400 via-red-500 to-amber-400 bg-clip-text text-transparent">
              NOWIND
            </span>
            <span className="block text-white/90 text-3xl sm:text-5xl lg:text-6xl mt-2 tracking-wide">
              LAST MINUTES
            </span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Niente vento ora? <span className="text-orange-300 font-bold">Il termico estivo arriva sempre</span>. Prenota la tua lezione lampo: ti chiamiamo appena il vento riprende.
          </p>
        </motion.div>

        {/* Info strip */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10"
        >
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
            <Clock className="w-5 h-5 text-orange-400 shrink-0" />
            <div className="text-xs">
              <div className="font-bold text-white">11:00 — 19:00</div>
              <div className="text-slate-400">Finestra giornaliera</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
            <Wind className="w-5 h-5 text-cyan-400 shrink-0" />
            <div className="text-xs">
              <div className="font-bold text-white">Termico estivo</div>
              <div className="text-slate-400">Sempre disponibile</div>
            </div>
          </div>
          <div className="col-span-2 md:col-span-1 flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
            <MapPin className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="text-xs">
              <div className="font-bold text-white">Saline di Olbia</div>
              <div className="text-slate-400">Sardegna</div>
            </div>
          </div>
        </motion.div>

        {/* PRICING CARDS */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-6 h-6 text-orange-400" />
            <h2 className="text-2xl sm:text-3xl font-black">Scegli il tuo pacchetto</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="promo-packages-grid">
            {PROMO_PACKAGES.map((pkg, i) => (
              <motion.button
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.05 }}
                onClick={() => { setSelected(pkg); document.getElementById('promo-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className={`text-left relative group overflow-hidden rounded-3xl p-5 border-2 transition-all ${
                  selected?.id === pkg.id
                    ? "border-orange-400 bg-orange-500/20 scale-[1.02] shadow-2xl shadow-orange-500/30"
                    : "border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10"
                }`}
                data-testid={`promo-pkg-${pkg.id}`}
              >
                <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${pkg.color} opacity-20 group-hover:opacity-40 transition-opacity blur-2xl`} />
                {pkg.badge && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest bg-gradient-to-r from-yellow-400 to-orange-500 text-black">
                    {pkg.badge}
                  </span>
                )}
                <div className="text-4xl mb-2">{pkg.icon}</div>
                <h3 className="font-black text-base mb-1 text-white">{pkg.title}</h3>
                <p className="text-xs text-slate-400 mb-4">{pkg.subtitle}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">€{pkg.promoPrice}</span>
                </div>
                {pkg.normalPrice && (
                  <p className="text-[11px] text-slate-500 mt-1">vs <span className="line-through">{pkg.normalPrice}</span></p>
                )}
                {selected?.id === pkg.id && (
                  <div className="absolute top-3 left-3 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                  </div>
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* FINE PRINT */}
        <div className="mb-10 p-5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-sm text-amber-100/90 leading-relaxed">
          <strong className="block mb-1 text-amber-300">⚡ Come funziona</strong>
          Il coupon è valido <strong>tutto il giorno</strong>: la scuola ti chiama appena c'è vento utile per fare lezione, tra le <strong>11:00 e le 19:00</strong>. Resta in zona spiaggia! Le lezioni promo sono riservate a chi inizia da zero.
        </div>

        {/* FORM */}
        <motion.form
          id="promo-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
          className="rounded-3xl bg-white/95 backdrop-blur-xl p-6 sm:p-8 shadow-2xl text-slate-900"
          data-testid="promo-form"
        >
          <h2 className="text-2xl sm:text-3xl font-black mb-2">
            {selected ? `Prenota: ${selected.title}` : "Compila per essere richiamato"}
          </h2>
          {selected ? (
            <p className="text-slate-600 mb-6">
              Prezzo: <strong className="text-orange-600 text-lg">€{selected.promoPrice}</strong>
            </p>
          ) : (
            <p className="text-slate-600 mb-6">👆 Seleziona prima un pacchetto qui sopra</p>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="p-name">Nome *</Label>
              <Input id="p-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Mario" data-testid="promo-name" />
            </div>
            <div>
              <Label htmlFor="p-surname">Cognome *</Label>
              <Input id="p-surname" value={form.surname} onChange={e => setForm({ ...form, surname: e.target.value })} placeholder="Rossi" data-testid="promo-surname" />
            </div>
            <div>
              <Label htmlFor="p-email">Email *</Label>
              <Input id="p-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="mario@email.com" data-testid="promo-email" />
            </div>
            <div>
              <Label htmlFor="p-phone">Telefono *</Label>
              <Input id="p-phone" type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+39 123 456 7890" data-testid="promo-phone" />
            </div>
            <div>
              <Label htmlFor="p-country">Paese *</Label>
              <select
                id="p-country"
                value={form.country}
                onChange={e => setForm({ ...form, country: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="promo-country"
              >
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="p-people">Persone</Label>
              <Input id="p-people" type="number" min="1" max="6" value={form.num_people} onChange={e => setForm({ ...form, num_people: parseInt(e.target.value) || 1 })} data-testid="promo-people" />
            </div>
          </div>

          <div className="mt-4">
            <Label htmlFor="p-notes">Note (facoltativo)</Label>
            <textarea
              id="p-notes"
              rows={2}
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Allergie, esperienza pregressa, info utili..."
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="promo-notes"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting || !selected}
            className="w-full mt-6 h-14 text-lg font-black bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 hover:from-orange-600 hover:to-red-600 text-white shadow-xl shadow-orange-500/40"
            data-testid="promo-submit"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Invio in corso...</>
            ) : (
              <><Flame className="w-5 h-5 mr-2" /> PRENDI IL COUPON</>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center mt-4 flex items-center justify-center gap-1">
            <Phone className="w-3 h-3" /> Ti chiamiamo o ti scriviamo su WhatsApp/SMS appena c'è vento.
          </p>
        </motion.form>

        {/* Footer */}
        <footer className="text-center mt-10 text-xs text-slate-500">
          <p>Sardinia Kite School · Move Sardinia A.S.D. · Saline di Olbia</p>
          <a href="https://sardiniakiteschool.net" className="text-orange-400 hover:underline">sardiniakiteschool.net</a>
        </footer>
      </div>
    </div>
  );
}
